import { lazy, Suspense, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { ROUTES } from '@/app/routes';
import { AppNav } from '@/components/layouts/AppNav';
import { findNavItem, useAppNav } from '@/components/layouts/AppNav/useAppNav';
import { AppTopBar } from '@/components/layouts/AppTopBar';
import { PageHeader } from '@/components/layouts/PageHeader';
import { displayNameOf } from '@/features/auth/domain/session';
import { useLogout } from '@/features/auth/hooks/useLogout';
import { useMessagesUnread } from '@/features/messages/hooks/useMessagesUnread';
import { usePresenceHeartbeat } from '@/features/messages/hooks/usePresenceHeartbeat';
import { useUnreadCount } from '@/features/notifications/hooks/useUnreadCount';
import { ScanCoachMark } from '@/features/onboarding/components/ScanCoachMark';
import { useScanCoachMark } from '@/features/onboarding/hooks/useScanCoachMark';
import { useAuthenticatedUser } from '@/features/auth/hooks/useSession';
import { useFormat } from '@/hooks/useFormat';
import { resolveAssetUrl } from '@/lib/assets';
import { cn } from '@/lib/cn';
import { NAV_LAYOUT_BY_FORMAT } from '@/tokens/breakpoints';

/**
 * Le tiroir n'est visible sur aucun premier rendu, quel que soit le format : il
 * Loaded on the first tap of the avatar, then kept mounted so its exit stays
 * animated. It has no business in the initial chunk.
 */
const AppDrawer = lazy(async () => ({
  default: (await import('@/components/layouts/AppDrawer')).AppDrawer,
}));

/**
 * The bell’s drop-down. Lazy for the same reason as the drawer: it is on no
 * first render, and it pulls the whole notifications feature with it.
 */
const NotificationPopover = lazy(async () => ({
  default: (
    await import('@/features/notifications/components/NotificationPopover')
  ).NotificationPopover,
}));

/**
 * The shell of the authenticated screens.
 *
 * It follows the navigation's own switch: a rail column on wide screens, a top
 * or bottom bar elsewhere with full-width content.
 *
 * Warning: on narrow formats the top bar and its account drawer are the only
 * way into the settings — the bottom bar has four slots.
 *
 * @see docs/features/ui-layout.md
 */
export function AppLayout() {
  const navigate = useNavigate();
  // Warning: here rather than on the screens that show presence. Somebody
  // reading their library is around too, and a heartbeat that only beat on
  // the messages screen would say they were not.
  usePresenceHeartbeat();
  const { t } = useTranslation('nav');
  const layout = NAV_LAYOUT_BY_FORMAT[useFormat()];
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Once opened the drawer stays mounted: unmounting on close would remove
  // the element before its exit animation could run.
  const [drawerMounted, setDrawerMounted] = useState(false);

  const handleScan = useCallback(() => {
    void navigate(ROUTES.scan);
  }, [navigate]);

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const toggleNotifications = useCallback(
    () => setNotificationsOpen((open) => !open),
    [],
  );
  const closeNotifications = useCallback(() => setNotificationsOpen(false), []);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const openDrawer = useCallback(() => {
    setDrawerMounted(true);
    setDrawerOpen(true);
  }, []);

  // `AppNav` reste rendu-pur : c'est cette couche-ci, et elle seule, qui a le
  // droit d'interroger une feature (le lint interdit l'inverse).
  const currentUser = useAuthenticatedUser();
  const { logout } = useLogout();
  const handleLogout = useCallback(() => void logout(), [logout]);

  const user = {
    username: currentUser.username,
    displayName: displayNameOf(currentUser),
    // Resolved once here, so the navigation gets a displayable URL and never
    // needs to know the API address.
    avatarUrl: resolveAssetUrl(currentUser.avatarUrl),
  };

  // `AppLayout` is the only layer allowed to read a feature, so the count is
  // fetched here and injected into the navigation, which stays pure.
  //
  const notifications = useUnreadCount();
  const messages = useMessagesUnread();
  const unread = {
    notifications: notifications.data?.unread ?? 0,
    messages: messages.data?.unread ?? 0,
  };

  // Le tiroir reprend ce qui ne tient pas dans la barre basse : ajouter une
  // `AppLayout` is the only layer allowed to read a feature, so household
  // membership is read here and injected into the navigation, which stays pure.
  const hasFamily = currentUser.familyId !== null;
  const { overflowItems } = useAppNav(layout, { hasFamily });

  /*
   * The single coach-mark of the first launch, on the phone's scan button.
   *
   * Only on the screen it opens on: pointing at the bottom bar from inside the
   * scanner, or from a conversation, is pointing at nothing anybody is looking
   * for. The condition also keeps the library count from being read on every
   * other screen.
   */
  const showScanCoach = useScanCoachMark(
    layout === 'tabbar' && pathname === ROUTES.dashboard,
  );

  /*
   * Which header a narrow screen gets, decided by how it was reached.
   *
   * A bottom-bar tab gets the top bar; a drawer entry gets a back header, since
   * what is missing there is the way back, not the avatar.
   */
  const navItem = layout === 'tabbar' ? findNavItem(pathname) : null;
  const dansLaBarreBasse = navItem?.layouts.includes('tabbar') ?? false;
  const showTopBar = navItem !== null && dansLaBarreBasse;
  const showPageHeader = navItem !== null && !dansLaBarreBasse;

  return (
    <div className={cn('min-h-dvh', layout === 'rail' && 'flex')}>
      <AppNav
        user={user}
        unread={unread}
        hasFamily={hasFamily}
        onScan={handleScan}
        scanHint={showScanCoach && <ScanCoachMark />}
        // Warning: the drop-down is offered on the wide formats only. On a
        // phone the bell goes straight to the page — a panel anchored to a
        // 375 px viewport is the page, minus the room to read it.
        onOpenNotifications={
          layout === 'tabbar' ? undefined : toggleNotifications
        }
        notificationsPanel={
          notificationsOpen && (
            <Suspense fallback={null}>
              <NotificationPopover
                // The rail keeps its bell at the bottom of the screen, so the
                // panel opens beside it; below, it fell off the bottom.
                placement={layout === 'rail' ? 'beside' : 'below'}
                onClose={closeNotifications}
              />
            </Suspense>
          )
        }
      />

      {showTopBar && (
        <AppTopBar
          user={user}
          unread={unread}
          // The dashboard shows the date and a greeting instead of a title.
          title={
            pathname === ROUTES.dashboard ? undefined : t(navItem.labelKey)
          }
          onOpenDrawer={openDrawer}
        />
      )}

      {/* Collé en haut comme la barre, et pour la même raison : sur ces
          écrans-là c'est le bouton retour qui doit rester à portée, sans avoir
          à remonter une liste d'envies entière.

          ⚠️ `pb-2` : sans lui le contenu vient toucher le bas du titre au
          moment où il passe dessous. */}
      {showPageHeader && (
        <div className="sticky top-0 z-30 bg-app-top px-4 pt-[calc(0.5rem+env(safe-area-inset-top))] pb-2">
          <PageHeader
            title={t(navItem.labelKey)}
            fallbackTo={ROUTES.dashboard}
          />
        </div>
      )}

      {layout === 'tabbar' && drawerMounted && (
        <Suspense fallback={null}>
          <AppDrawer
            open={drawerOpen}
            onClose={closeDrawer}
            user={{
              ...user,
              subtitle: currentUser.email ?? currentUser.username,
            }}
            items={overflowItems}
            onLogout={handleLogout}
          />
        </Suspense>
      )}

      <main
        className={cn(
          'min-w-0 flex-1',
          // The bottom bar is `fixed`, so without this reserve it covers the
          // end of the content. The notch reserve is only needed when the top
          // bar does not already carry it.
          layout === 'tabbar' &&
            'px-4 pb-[calc(4rem+env(safe-area-inset-bottom))]',
          layout === 'tabbar' &&
            (showTopBar || showPageHeader
              ? 'pt-0'
              : 'pt-[calc(1.5rem+env(safe-area-inset-top))]'),
          // Warning: no maximum width here. The top bar is full-bleed, so a
          // capped, centred content left a visible gap on its left. Each screen
          // bounds its own reading column instead.
          (layout === 'topbar' || layout === 'rail') && 'px-6 py-6',
        )}
      >
        <Outlet />
      </main>
    </div>
  );
}
